import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  approveListing as approveListingApi,
  getPartnerListings,
  getPendingListings,
  rejectListing as rejectListingApi,
  setListingFeatured,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';

// The listings come from our own server rather than the SDK: pending listings
// belong to other users, which only the Integration API can read
// (server/api/admin/listing-review.js). That is every pending listing, newest
// first; the page splits them into "Venter" and "Sendt tilbake" by whether
// they carry a rejection.

// ================ Async thunks ================ //

export const fetchPendingListingsThunk = createAsyncThunk(
  'ListingAdminPage/fetchPendingListings',
  (_, { rejectWithValue }) => {
    return getPendingListings()
      .then(body => denormalisedResponseEntities({ data: body }))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const fetchPendingListings = () => dispatch => {
  return dispatch(fetchPendingListingsThunk()).unwrap();
};

export const approveListingThunk = createAsyncThunk(
  'ListingAdminPage/approveListing',
  ({ listingId }, { rejectWithValue }) => {
    return (
      approveListingApi(listingId)
        // `queued`: a Zuprize Partner's listing, now in its release queue.
        .then(response => ({ listingId, queued: !!response?.queued }))
        .catch(e => rejectWithValue(storableError(e)))
    );
  }
);
export const approveListing = listingId => dispatch => {
  return dispatch(approveListingThunk({ listingId })).unwrap();
};

/**
 * Send a listing back to the seller. Resolves `{ listingId, emailSent, rejection }`:
 * the rejection is saved even when the email fails, and the page tells the
 * admin which of the two happened.
 */
export const rejectListingThunk = createAsyncThunk(
  'ListingAdminPage/rejectListing',
  ({ listingId, reason, personalMessage }, { rejectWithValue }) => {
    return rejectListingApi(listingId, { reason, personalMessage })
      .then(response => ({
        listingId,
        emailSent: !!response?.emailSent,
        rejection: response?.rejection || null,
      }))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const rejectListing = params => dispatch => {
  return dispatch(rejectListingThunk(params)).unwrap();
};

// The Butikker tab: verified partners' published listings, each with the
// operator-only `featuredOnButikker` flag that puts it in the hand-picked
// carousel on the Butikker page.
export const FEATURED_KEY = 'featuredOnButikker';

export const fetchPartnerListingsThunk = createAsyncThunk(
  'ListingAdminPage/fetchPartnerListings',
  (_, { rejectWithValue }) => {
    return getPartnerListings()
      .then(body => denormalisedResponseEntities({ data: body }))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const fetchPartnerListings = () => dispatch => {
  return dispatch(fetchPartnerListingsThunk()).unwrap();
};

export const setFeaturedThunk = createAsyncThunk(
  'ListingAdminPage/setFeatured',
  ({ listingId, featured }, { rejectWithValue }) => {
    return setListingFeatured(listingId, featured)
      .then(() => ({ listingId, featured }))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const setFeatured = (listingId, featured) => dispatch => {
  return dispatch(setFeaturedThunk({ listingId, featured })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  listings: [],
  fetchInProgress: false,
  fetchError: null,
  // uuid of the listing whose approval is on its way, so only that row spins
  approvingListingId: null,
  approveError: null,
  rejectInProgress: false,
  rejectError: null,
  partnerListings: [],
  partnerFetchInProgress: false,
  partnerFetchError: null,
  // uuid of the listing whose featured flag is being saved
  featuringListingId: null,
  featureError: null,
};

// An approved listing is published and leaves the panel altogether.
const withoutListing = (listings, listingId) => listings.filter(l => l.id.uuid !== listingId);

const listingAdminPageSlice = createSlice({
  name: 'ListingAdminPage',
  initialState,
  reducers: {
    clearRejectError: state => {
      state.rejectError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPendingListingsThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchPendingListingsThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.listings = action.payload;
      })
      .addCase(fetchPendingListingsThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })

      .addCase(approveListingThunk.pending, (state, action) => {
        state.approvingListingId = action.meta.arg.listingId;
        state.approveError = null;
      })
      .addCase(approveListingThunk.fulfilled, (state, action) => {
        state.approvingListingId = null;
        state.listings = withoutListing(state.listings, action.payload.listingId);
      })
      .addCase(approveListingThunk.rejected, (state, action) => {
        state.approvingListingId = null;
        state.approveError = action.payload;
      })

      .addCase(rejectListingThunk.pending, state => {
        state.rejectInProgress = true;
        state.rejectError = null;
      })
      .addCase(rejectListingThunk.fulfilled, (state, action) => {
        state.rejectInProgress = false;
        // A rejected listing stays: stamping the rejection on it is what moves
        // it from "Venter" to "Sendt tilbake".
        const { listingId, rejection } = action.payload;
        const listing = state.listings.find(l => l.id.uuid === listingId);
        if (listing && rejection) {
          listing.attributes.metadata = { ...listing.attributes.metadata, rejection };
        } else {
          state.listings = withoutListing(state.listings, listingId);
        }
      })
      .addCase(rejectListingThunk.rejected, (state, action) => {
        state.rejectInProgress = false;
        state.rejectError = action.payload;
      })

      .addCase(fetchPartnerListingsThunk.pending, state => {
        state.partnerFetchInProgress = true;
        state.partnerFetchError = null;
      })
      .addCase(fetchPartnerListingsThunk.fulfilled, (state, action) => {
        state.partnerFetchInProgress = false;
        state.partnerListings = action.payload;
      })
      .addCase(fetchPartnerListingsThunk.rejected, (state, action) => {
        state.partnerFetchInProgress = false;
        state.partnerFetchError = action.payload;
      })

      .addCase(setFeaturedThunk.pending, (state, action) => {
        state.featuringListingId = action.meta.arg.listingId;
        state.featureError = null;
      })
      .addCase(setFeaturedThunk.fulfilled, (state, action) => {
        state.featuringListingId = null;
        const { listingId, featured } = action.payload;
        const listing = state.partnerListings.find(l => l.id.uuid === listingId);
        if (listing) {
          listing.attributes.metadata = {
            ...listing.attributes.metadata,
            [FEATURED_KEY]: featured,
          };
        }
      })
      .addCase(setFeaturedThunk.rejected, (state, action) => {
        state.featuringListingId = null;
        state.featureError = action.payload;
      });
  },
});

export const { clearRejectError } = listingAdminPageSlice.actions;
export default listingAdminPageSlice.reducer;

// ================ Load data ================ //

export const loadData = () => dispatch => {
  // The page decides whether to render or redirect from the current user's
  // admin flag, so that has to be in the store before the queue matters. A
  // non-admin's request would only be refused by the server anyway.
  return dispatch(fetchCurrentUser()).then(() => {
    return Promise.all([
      dispatch(fetchPendingListings()).catch(() => {
        // Shown on the page from `fetchError`; nothing more to do here.
      }),
      dispatch(fetchPartnerListings()).catch(() => {
        // Shown in the Butikker tab from `partnerFetchError`.
      }),
    ]);
  });
};
